import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAdminAppLinks, useUpdateAdminAppLinks } from "@/hooks/useAppLinks";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";
import type { AdminAppLinks } from "@/types/app-links";
import {
  createAppLinksSchema,
  type AppLinksFormValues,
} from "@/components/pages/app-links-settings/utils/appLinksSchema";

interface AppLinkRoleConfig {
  roleKey: "tenant" | "frontDesk" | "admin" | "staff" | "guest";
  knowledgeBaseField: keyof AppLinksFormValues;
  customerSupportField: keyof AppLinksFormValues;
}

const APP_LINK_ROLE_CONFIGS: AppLinkRoleConfig[] = [
  {
    roleKey: "tenant",
    knowledgeBaseField: "tenantKnowledgeBaseUrl",
    customerSupportField: "tenantCustomerSupportUrl",
  },
  {
    roleKey: "frontDesk",
    knowledgeBaseField: "frontDeskKnowledgeBaseUrl",
    customerSupportField: "frontDeskCustomerSupportUrl",
  },
  {
    roleKey: "admin",
    knowledgeBaseField: "adminKnowledgeBaseUrl",
    customerSupportField: "adminCustomerSupportUrl",
  },
  {
    roleKey: "staff",
    knowledgeBaseField: "staffKnowledgeBaseUrl",
    customerSupportField: "staffCustomerSupportUrl",
  },
  {
    roleKey: "guest",
    knowledgeBaseField: "guestKnowledgeBaseUrl",
    customerSupportField: "guestCustomerSupportUrl",
  },
];

const DEFAULT_VALUES: AppLinksFormValues = {
  tenantKnowledgeBaseUrl: "",
  tenantCustomerSupportUrl: "",
  frontDeskKnowledgeBaseUrl: "",
  frontDeskCustomerSupportUrl: "",
  adminKnowledgeBaseUrl: "",
  adminCustomerSupportUrl: "",
  staffKnowledgeBaseUrl: "",
  staffCustomerSupportUrl: "",
  guestKnowledgeBaseUrl: "",
  guestCustomerSupportUrl: "",
};

const mapAppLinksToFormValues = (data?: AdminAppLinks): AppLinksFormValues => ({
  tenantKnowledgeBaseUrl: data?.tenantKnowledgeBaseUrl ?? "",
  tenantCustomerSupportUrl: data?.tenantCustomerSupportUrl ?? "",
  frontDeskKnowledgeBaseUrl: data?.frontDeskKnowledgeBaseUrl ?? "",
  frontDeskCustomerSupportUrl: data?.frontDeskCustomerSupportUrl ?? "",
  adminKnowledgeBaseUrl: data?.adminKnowledgeBaseUrl ?? "",
  adminCustomerSupportUrl: data?.adminCustomerSupportUrl ?? "",
  staffKnowledgeBaseUrl: data?.staffKnowledgeBaseUrl ?? "",
  staffCustomerSupportUrl: data?.staffCustomerSupportUrl ?? "",
  guestKnowledgeBaseUrl: data?.guestKnowledgeBaseUrl ?? "",
  guestCustomerSupportUrl: data?.guestCustomerSupportUrl ?? "",
});

const mapFormValuesToPayload = (values: AppLinksFormValues): AdminAppLinks => ({
  tenantKnowledgeBaseUrl: values.tenantKnowledgeBaseUrl.trim() || null,
  tenantCustomerSupportUrl: values.tenantCustomerSupportUrl.trim() || null,
  frontDeskKnowledgeBaseUrl: values.frontDeskKnowledgeBaseUrl.trim() || null,
  frontDeskCustomerSupportUrl:
    values.frontDeskCustomerSupportUrl.trim() || null,
  adminKnowledgeBaseUrl: values.adminKnowledgeBaseUrl.trim() || null,
  adminCustomerSupportUrl: values.adminCustomerSupportUrl.trim() || null,
  staffKnowledgeBaseUrl: values.staffKnowledgeBaseUrl.trim() || null,
  staffCustomerSupportUrl: values.staffCustomerSupportUrl.trim() || null,
  guestKnowledgeBaseUrl: values.guestKnowledgeBaseUrl.trim() || null,
  guestCustomerSupportUrl: values.guestCustomerSupportUrl.trim() || null,
});

export const AppLinksSettingsPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { canAdminister } = usePlatformPermissions();
  const canWrite = canAdminister("settings");
  const appLinksSchema = React.useMemo(() => createAppLinksSchema(t), [t]);
  const { data, isLoading, isError } = useAdminAppLinks();
  const updateAdminAppLinks = useUpdateAdminAppLinks();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<AppLinksFormValues>({
    resolver: zodResolver(appLinksSchema),
    defaultValues: DEFAULT_VALUES,
  });

  React.useEffect(() => {
    if (data) {
      reset(mapAppLinksToFormValues(data));
    }
  }, [data, reset]);

  const onSubmit = (values: AppLinksFormValues) => {
    updateAdminAppLinks.mutate(mapFormValuesToPayload(values), {
      onSuccess: (response) => {
        reset(mapAppLinksToFormValues(response));
        toast.success(t("common.appLinksSettings.toast.success"));
      },
      onError: () => {
        toast.error(t("common.appLinksSettings.toast.error"));
      },
    });
  };

  if (isLoading) {
    return (
      <div className="p-12 text-sm text-muted-foreground">
        {t("common.appLinksSettings.loading")}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-12 text-sm text-destructive">
        {t("common.appLinksSettings.loadError")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-12">
      <header className="flex max-w-3xl flex-col gap-2">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("common.appLinksSettings.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("common.appLinksSettings.description")}
        </p>
      </header>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex max-w-5xl flex-col gap-6"
      >
        {APP_LINK_ROLE_CONFIGS.map((config) => (
          <Card key={config.roleKey}>
            <CardHeader>
              <CardTitle>
                {t(`common.appLinksSettings.roles.${config.roleKey}`)}
              </CardTitle>
              <CardDescription>
                {t("common.appLinksSettings.roleDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Input
                  type="url"
                  label={t("common.appLinksSettings.fields.knowledgeBase")}
                  placeholder={t(
                    "common.appLinksSettings.fields.urlPlaceholder",
                  )}
                  aria-invalid={
                    errors[config.knowledgeBaseField] ? true : false
                  }
                  {...register(config.knowledgeBaseField)}
                />
                {errors[config.knowledgeBaseField]?.message ? (
                  <p className="text-sm text-destructive">
                    {errors[config.knowledgeBaseField]?.message}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-2">
                <Input
                  type="url"
                  label={t("common.appLinksSettings.fields.customerSupport")}
                  placeholder={t(
                    "common.appLinksSettings.fields.urlPlaceholder",
                  )}
                  aria-invalid={
                    errors[config.customerSupportField] ? true : false
                  }
                  {...register(config.customerSupportField)}
                />
                {errors[config.customerSupportField]?.message ? (
                  <p className="text-sm text-destructive">
                    {errors[config.customerSupportField]?.message}
                  </p>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}

        <Card>
          <CardFooter className="justify-end">
            <Button
              type="submit"
              variant="new_default"
              disabled={!canWrite || updateAdminAppLinks.isPending || !isDirty}
              title={
                !canWrite
                  ? t(
                      "common.insufficientPermissions",
                      "Insufficient permissions",
                    )
                  : undefined
              }
            >
              {updateAdminAppLinks.isPending
                ? t("common.appLinksSettings.saving")
                : t("common.appLinksSettings.save")}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
};
